// Frontend permissions utility that mirrors the backend permission system

// User roles (matching backend)
export const UserRole = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  ACCOUNTANT: 'accountant',
  TEACHER: 'teacher'
};

// Permissions (matching backend)
export const Permission = {
  // User management
  MANAGE_USERS: 'manage_users',
  
  // Student management
  CREATE_STUDENT: 'create_student',
  READ_STUDENT: 'read_student',
  UPDATE_STUDENT: 'update_student',
  DELETE_STUDENT: 'delete_student',
  SEARCH_STUDENT: 'search_student',
  VIEW_STUDENTS: 'view_students',
  CREATE_STUDENTS: 'create_students',
  EDIT_STUDENTS: 'edit_students',
  
  // Batch management
  CREATE_BATCH: 'create_batch',
  READ_BATCH: 'read_batch',
  UPDATE_BATCH: 'update_batch',
  DELETE_BATCH: 'delete_batch',
  VIEW_BATCHES: 'view_batches',
  CREATE_BATCHES: 'create_batches',
  EDIT_BATCHES: 'edit_batches',
  
  // Enrollment management
  CREATE_ENROLLMENT: 'create_enrollment',
  READ_ENROLLMENT: 'read_enrollment',
  UPDATE_ENROLLMENT: 'update_enrollment',
  DELETE_ENROLLMENT: 'delete_enrollment',
  MANAGE_ENROLLMENT_FEES: 'manage_enrollment_fees',
  VIEW_ENROLLMENTS: 'view_enrollments',
  CREATE_ENROLLMENTS: 'create_enrollments',
  
  // Fee management
  CREATE_FEE_STRUCTURE: 'create_fee_structure',
  READ_FEE_STRUCTURE: 'read_fee_structure',
  UPDATE_FEE_STRUCTURE: 'update_fee_structure',
  DELETE_FEE_STRUCTURE: 'delete_fee_structure',
  MANAGE_PAYMENTS: 'manage_payments',
  VIEW_PAYMENTS: 'view_payments',
  CLEANUP_FEES: 'cleanup_fees',
  VIEW_FEES: 'view_fees',
  CREATE_FEES: 'create_fees',
  EDIT_FEES: 'edit_fees',
  
  // Activity management
  CREATE_ACTIVITY: 'create_activity',
  READ_ACTIVITY: 'read_activity',
  
  // Enquiry management
  CREATE_ENQUIRY: 'create_enquiry',
  READ_ENQUIRY: 'read_enquiry',
  UPDATE_ENQUIRY: 'update_enquiry',
  DELETE_ENQUIRY: 'delete_enquiry',
  VIEW_ENQUIRIES: 'view_enquiries',
  CREATE_ENQUIRIES: 'create_enquiries',
  
  // Workshop management
  CREATE_WORKSHOP: 'create_workshop',
  READ_WORKSHOP: 'read_workshop',
  UPDATE_WORKSHOP: 'update_workshop',
  DELETE_WORKSHOP: 'delete_workshop',
  MANAGE_WORKSHOP_REGISTRATIONS: 'manage_workshop_registrations',
  MANAGE_WORKSHOP_ATTENDANCE: 'manage_workshop_attendance',
  VIEW_WORKSHOP_ATTENDANCE: 'view_workshop_attendance',
  VIEW_WORKSHOPS: 'view_workshops',
  CREATE_WORKSHOPS: 'create_workshops',
  EDIT_WORKSHOPS: 'edit_workshops',
  
  // Task management
  CREATE_TASK: 'create_task',
  READ_TASK: 'read_task',
  UPDATE_TASK: 'update_task',
  DELETE_TASK: 'delete_task',
  COMMENT_TASK: 'comment_task',
  VIEW_TASKS: 'view_tasks',

  // Employee management
  CREATE_EMPLOYEE: 'create_employee',
  READ_EMPLOYEE: 'read_employee',
  UPDATE_EMPLOYEE: 'update_employee',
  DELETE_EMPLOYEE: 'delete_employee',
  SEARCH_EMPLOYEE: 'search_employee',
  VIEW_EMPLOYEES: 'view_employees',
  CREATE_EMPLOYEES: 'create_employees',
  EDIT_EMPLOYEES: 'edit_employees',

  // Payroll management
  CREATE_PAYROLL: 'create_payroll',
  READ_PAYROLL: 'read_payroll',
  UPDATE_PAYROLL: 'update_payroll',
  DELETE_PAYROLL: 'delete_payroll',
  FINALIZE_PAYROLL: 'finalize_payroll',
  PAY_PAYROLL: 'pay_payroll',
  VIEW_PAYROLL: 'view_payroll',

  // Test management
  CREATE_TEST: 'create_test',
  READ_TEST: 'read_test',
  UPDATE_TEST: 'update_test',
  DELETE_TEST: 'delete_test',
  VIEW_TEST_RESULTS: 'view_test_results',
  VIEW_LEADERBOARD: 'view_leaderboard',
  VIEW_TESTS: 'view_tests',
  CREATE_TESTS: 'create_tests',
  EDIT_TESTS: 'edit_tests',

  // Invitation management
  VIEW_INVITATIONS: 'view_invitations',
  CREATE_INVITATIONS: 'create_invitations',
  
  // Organization management
  VIEW_ORGANIZATION: 'view_organization',
  
  // Dashboard and reports
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_REPORTS: 'view_reports',
  
  // Profile
  VIEW_PROFILE: 'view_profile',

  // Chat
  VIEW_CHAT: 'view_chat',
  SEND_MESSAGE: 'send_message',
};

// Role permissions mapping (matching backend)
export const ROLE_PERMISSIONS = {
  [UserRole.ADMIN]: [
    // Full access to everything including user management
    Permission.MANAGE_USERS,
    Permission.CREATE_STUDENT, Permission.READ_STUDENT, Permission.UPDATE_STUDENT, Permission.DELETE_STUDENT, Permission.SEARCH_STUDENT,
    Permission.VIEW_STUDENTS, Permission.CREATE_STUDENTS, Permission.EDIT_STUDENTS,
    Permission.CREATE_BATCH, Permission.READ_BATCH, Permission.UPDATE_BATCH, Permission.DELETE_BATCH,
    Permission.VIEW_BATCHES, Permission.CREATE_BATCHES, Permission.EDIT_BATCHES,
    Permission.CREATE_ENROLLMENT, Permission.READ_ENROLLMENT, Permission.UPDATE_ENROLLMENT, Permission.DELETE_ENROLLMENT, Permission.MANAGE_ENROLLMENT_FEES,
    Permission.VIEW_ENROLLMENTS, Permission.CREATE_ENROLLMENTS,
    Permission.CREATE_FEE_STRUCTURE, Permission.READ_FEE_STRUCTURE, Permission.UPDATE_FEE_STRUCTURE, Permission.DELETE_FEE_STRUCTURE, Permission.MANAGE_PAYMENTS, Permission.VIEW_PAYMENTS, Permission.CLEANUP_FEES,
    Permission.VIEW_FEES, Permission.CREATE_FEES, Permission.EDIT_FEES,
    Permission.CREATE_ACTIVITY, Permission.READ_ACTIVITY,
    Permission.CREATE_ENQUIRY, Permission.READ_ENQUIRY, Permission.UPDATE_ENQUIRY, Permission.DELETE_ENQUIRY,
    Permission.VIEW_ENQUIRIES, Permission.CREATE_ENQUIRIES,
    Permission.CREATE_WORKSHOP, Permission.READ_WORKSHOP, Permission.UPDATE_WORKSHOP, Permission.DELETE_WORKSHOP, Permission.MANAGE_WORKSHOP_REGISTRATIONS, Permission.MANAGE_WORKSHOP_ATTENDANCE, Permission.VIEW_WORKSHOP_ATTENDANCE,
    Permission.VIEW_WORKSHOPS, Permission.CREATE_WORKSHOPS, Permission.EDIT_WORKSHOPS,
    Permission.CREATE_TASK, Permission.READ_TASK, Permission.UPDATE_TASK, Permission.DELETE_TASK, Permission.COMMENT_TASK,
    Permission.VIEW_TASKS,
    Permission.CREATE_EMPLOYEE, Permission.READ_EMPLOYEE, Permission.UPDATE_EMPLOYEE, Permission.DELETE_EMPLOYEE, Permission.SEARCH_EMPLOYEE,
    Permission.VIEW_EMPLOYEES, Permission.CREATE_EMPLOYEES, Permission.EDIT_EMPLOYEES,
    Permission.CREATE_PAYROLL, Permission.READ_PAYROLL, Permission.UPDATE_PAYROLL, Permission.DELETE_PAYROLL, Permission.FINALIZE_PAYROLL, Permission.PAY_PAYROLL, Permission.VIEW_PAYROLL,
    Permission.CREATE_TEST, Permission.READ_TEST, Permission.UPDATE_TEST, Permission.DELETE_TEST, Permission.VIEW_TEST_RESULTS, Permission.VIEW_LEADERBOARD,
    Permission.VIEW_TESTS, Permission.CREATE_TESTS, Permission.EDIT_TESTS,
    Permission.VIEW_INVITATIONS, Permission.CREATE_INVITATIONS,
    Permission.VIEW_ORGANIZATION,
    Permission.VIEW_DASHBOARD, Permission.VIEW_REPORTS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_CHAT, Permission.SEND_MESSAGE,
  ],

  [UserRole.MANAGER]: [
    // Full access except user management (invitations)
    Permission.CREATE_STUDENT, Permission.READ_STUDENT, Permission.UPDATE_STUDENT, Permission.DELETE_STUDENT, Permission.SEARCH_STUDENT,
    Permission.VIEW_STUDENTS, Permission.CREATE_STUDENTS, Permission.EDIT_STUDENTS,
    Permission.CREATE_BATCH, Permission.READ_BATCH, Permission.UPDATE_BATCH, Permission.DELETE_BATCH,
    Permission.VIEW_BATCHES, Permission.CREATE_BATCHES, Permission.EDIT_BATCHES,
    Permission.CREATE_ENROLLMENT, Permission.READ_ENROLLMENT, Permission.UPDATE_ENROLLMENT, Permission.DELETE_ENROLLMENT, Permission.MANAGE_ENROLLMENT_FEES,
    Permission.VIEW_ENROLLMENTS, Permission.CREATE_ENROLLMENTS,
    Permission.CREATE_FEE_STRUCTURE, Permission.READ_FEE_STRUCTURE, Permission.UPDATE_FEE_STRUCTURE, Permission.DELETE_FEE_STRUCTURE, Permission.MANAGE_PAYMENTS, Permission.VIEW_PAYMENTS, Permission.CLEANUP_FEES,
    Permission.VIEW_FEES, Permission.CREATE_FEES, Permission.EDIT_FEES,
    Permission.CREATE_ACTIVITY, Permission.READ_ACTIVITY,
    Permission.CREATE_ENQUIRY, Permission.READ_ENQUIRY, Permission.UPDATE_ENQUIRY, Permission.DELETE_ENQUIRY,
    Permission.VIEW_ENQUIRIES, Permission.CREATE_ENQUIRIES,
    Permission.CREATE_WORKSHOP, Permission.READ_WORKSHOP, Permission.UPDATE_WORKSHOP, Permission.DELETE_WORKSHOP, Permission.MANAGE_WORKSHOP_REGISTRATIONS, Permission.MANAGE_WORKSHOP_ATTENDANCE, Permission.VIEW_WORKSHOP_ATTENDANCE,
    Permission.VIEW_WORKSHOPS, Permission.CREATE_WORKSHOPS, Permission.EDIT_WORKSHOPS,
    Permission.CREATE_TASK, Permission.READ_TASK, Permission.UPDATE_TASK, Permission.DELETE_TASK, Permission.COMMENT_TASK,
    Permission.VIEW_TASKS,
    Permission.CREATE_TEST, Permission.READ_TEST, Permission.UPDATE_TEST, Permission.DELETE_TEST, Permission.VIEW_TEST_RESULTS, Permission.VIEW_LEADERBOARD,
    Permission.VIEW_TESTS, Permission.CREATE_TESTS, Permission.EDIT_TESTS,
    Permission.VIEW_ORGANIZATION,
    Permission.VIEW_DASHBOARD, Permission.VIEW_REPORTS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_CHAT, Permission.SEND_MESSAGE,
  ],

  [UserRole.ACCOUNTANT]: [
    // Full access to fees and payments, view-only for others
    Permission.READ_STUDENT, Permission.SEARCH_STUDENT,
    Permission.VIEW_STUDENTS,
    Permission.READ_BATCH,
    Permission.VIEW_BATCHES,
    Permission.READ_ENROLLMENT,
    Permission.VIEW_ENROLLMENTS,
    Permission.CREATE_FEE_STRUCTURE, Permission.READ_FEE_STRUCTURE, Permission.UPDATE_FEE_STRUCTURE, Permission.DELETE_FEE_STRUCTURE, Permission.MANAGE_PAYMENTS, Permission.VIEW_PAYMENTS, Permission.CLEANUP_FEES,
    Permission.VIEW_FEES,
    Permission.READ_ACTIVITY,
    Permission.READ_ENQUIRY,
    Permission.VIEW_ENQUIRIES,
    Permission.READ_WORKSHOP, Permission.MANAGE_WORKSHOP_REGISTRATIONS, Permission.MANAGE_WORKSHOP_ATTENDANCE, Permission.VIEW_WORKSHOP_ATTENDANCE,
    Permission.VIEW_WORKSHOPS,
    Permission.READ_TASK, Permission.CREATE_TASK, Permission.UPDATE_TASK, Permission.DELETE_TASK, Permission.COMMENT_TASK,
    Permission.VIEW_TASKS,
    Permission.READ_PAYROLL, Permission.PAY_PAYROLL, Permission.VIEW_PAYROLL,
    Permission.VIEW_ORGANIZATION,
    Permission.VIEW_DASHBOARD, Permission.VIEW_REPORTS,
    Permission.VIEW_PROFILE,
    Permission.VIEW_CHAT, Permission.SEND_MESSAGE,
  ],

  [UserRole.TEACHER]: [
    // Only test management permissions
    Permission.CREATE_TEST, Permission.READ_TEST, Permission.UPDATE_TEST, Permission.DELETE_TEST, Permission.VIEW_TEST_RESULTS, Permission.VIEW_LEADERBOARD,
    Permission.VIEW_TESTS, Permission.CREATE_TESTS, Permission.EDIT_TESTS,
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_PROFILE,
    Permission.VIEW_CHAT, Permission.SEND_MESSAGE,
  ],
};

// Permission checking functions
export const hasPermission = (userRole, permission) => {
  console.log('🔑 hasPermission called with:', { userRole, permission });
  
  if (!userRole || !permission) {
    console.log('❌ Missing userRole or permission');
    return false;
  }
  
  const permissions = ROLE_PERMISSIONS[userRole] || [];
  console.log('📋 User permissions:', { userRole, permissions });
  
  const hasAccess = permissions.includes(permission);
  console.log('✅ Permission check result:', { permission, hasAccess });
  
  return hasAccess;
};

export const getUserPermissions = (userRole) => {
  if (!userRole) return [];
  return ROLE_PERMISSIONS[userRole] || [];
};

// Resource-action mapping for route-based permissions
export const RESOURCE_PERMISSIONS = {
  // Dashboard (root route)
  '/': Permission.VIEW_DASHBOARD,
  '/dashboard': Permission.VIEW_DASHBOARD,
  
  // Students
  '/students': Permission.VIEW_STUDENTS,
  '/students/new': Permission.CREATE_STUDENTS,
  '/students/:id': Permission.VIEW_STUDENTS,
  '/students/:id/edit': Permission.EDIT_STUDENTS,
  
  // Batches
  '/batches': Permission.VIEW_BATCHES,
  '/batches/new': Permission.CREATE_BATCHES,
  '/batches/:id': Permission.VIEW_BATCHES,
  '/batches/:id/edit': Permission.EDIT_BATCHES,
  
  // Enrollments
  '/enrollments': Permission.VIEW_ENROLLMENTS,
  '/enrollments/new': Permission.CREATE_ENROLLMENTS,
  
  // Fees
  '/fees': Permission.VIEW_FEES,
  '/fees/new': Permission.CREATE_FEES,
  '/fees/:id': Permission.VIEW_FEES,
  '/fees/:id/edit': Permission.EDIT_FEES,
  '/fees/:feeStructureId': Permission.VIEW_FEES,
  '/fees/:feeStructureId/edit': Permission.EDIT_FEES,
  '/fees/:feeStructureId/payments': Permission.VIEW_PAYMENTS,
  '/fees/:feeStructureId/payments/new': Permission.MANAGE_PAYMENTS,
  
  // Payments
  '/payments': Permission.VIEW_PAYMENTS,
  
  // Workshops
  '/workshops': Permission.VIEW_WORKSHOPS,
  '/workshops/new': Permission.CREATE_WORKSHOPS,
  '/workshops/:id': Permission.VIEW_WORKSHOPS,
  '/workshops/:id/edit': Permission.EDIT_WORKSHOPS,
  '/workshops/:id/qr-code': Permission.VIEW_WORKSHOPS,
  '/workshops/:id/attendance-qr': Permission.VIEW_WORKSHOP_ATTENDANCE,
  '/workshops/:id/attendance': Permission.VIEW_WORKSHOP_ATTENDANCE,
  
  // Tasks
  '/tasks': Permission.VIEW_TASKS,

  // Employees
  '/employees': Permission.VIEW_EMPLOYEES,
  '/employees/new': Permission.CREATE_EMPLOYEES,
  '/employees/:id': Permission.VIEW_EMPLOYEES,
  '/employees/:id/edit': Permission.EDIT_EMPLOYEES,

  // Employee Attendance
  '/employee-attendance': Permission.VIEW_EMPLOYEES,
  '/employee-attendance/records': Permission.VIEW_EMPLOYEES,

  // Student Attendance
  '/student-attendance': Permission.VIEW_STUDENTS,
  '/student-attendance/records': Permission.VIEW_STUDENTS,
  '/student-attendance/analytics': Permission.VIEW_STUDENTS,

  // Tests
  '/tests': Permission.VIEW_TESTS,
  '/tests/new': Permission.CREATE_TESTS,
  '/tests/:testId': Permission.VIEW_TESTS,
  '/top-performers': Permission.VIEW_LEADERBOARD,

  // Invitations
  '/invitations': Permission.VIEW_INVITATIONS,
  '/invitations/new': Permission.CREATE_INVITATIONS,
  
  // Enquiries
  '/enquiries': Permission.VIEW_ENQUIRIES,
  '/enquiries/new': Permission.CREATE_ENQUIRIES,
  '/enquiries/:id': Permission.READ_ENQUIRY,
  '/enquiries/:id/edit': Permission.UPDATE_ENQUIRY,
  
  // Organization
  '/organization': Permission.VIEW_ORGANIZATION,
  
  // Profile (accessible to all authenticated users)
  '/profile': Permission.VIEW_PROFILE,
};

// Check if user can access a specific route
export const canAccessRoute = (userRole, pathname) => {
  console.log('🔐 canAccessRoute called with:', { userRole, pathname });
  
  if (!userRole || !pathname) {
    console.log('❌ Missing userRole or pathname');
    return false;
  }
  
  // Check exact matches first
  if (RESOURCE_PERMISSIONS[pathname] !== undefined) {
    const permission = RESOURCE_PERMISSIONS[pathname];
    console.log('✅ Exact match found:', { pathname, permission });
    const hasAccess = permission === null ? true : hasPermission(userRole, permission);
    console.log('🔑 Access check result:', { hasAccess, userRole, permission });
    return hasAccess;
  }
  
  console.log('❌ No exact match, checking patterns...');
  console.log('🔍 Available routes:', Object.keys(RESOURCE_PERMISSIONS));
  
  // Check pattern matches (for routes with parameters)
  for (const [pattern, permission] of Object.entries(RESOURCE_PERMISSIONS)) {
    if (pattern.includes(':')) {
      // Convert pattern to regex
      const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${regexPattern}$`);
      if (regex.test(pathname)) {
        console.log('✅ Pattern match found:', { pattern, pathname, permission });
        const hasAccess = permission === null ? true : hasPermission(userRole, permission);
        console.log('🔑 Access check result:', { hasAccess, userRole, permission });
        return hasAccess;
      }
    }
  }
  
  console.log('❌ No pattern matches found, access denied');
  return false;
};

// Navigation item permissions
export const NAVIGATION_PERMISSIONS = {
  'Dashboard': Permission.VIEW_DASHBOARD,
  'Students': Permission.VIEW_STUDENTS,
  'Batches': Permission.VIEW_BATCHES,
  'Enrollments': Permission.VIEW_ENROLLMENTS,
  'Fees': Permission.VIEW_FEES,
  'Payments': Permission.VIEW_PAYMENTS,
  'Workshops': Permission.VIEW_WORKSHOPS,
  'Tasks': Permission.VIEW_TASKS,
  'Tests': Permission.VIEW_TESTS,
  'Top Performers': Permission.VIEW_LEADERBOARD,
  'Employees': Permission.VIEW_EMPLOYEES,
  'Employee Attendance': Permission.VIEW_EMPLOYEES,
  'Student Attendance': Permission.VIEW_STUDENTS,
  'Invitations': Permission.VIEW_INVITATIONS,
  'Enquiries': Permission.VIEW_ENQUIRIES,
  'Organization': Permission.VIEW_ORGANIZATION,
  'Profile': Permission.VIEW_PROFILE,
  'Chat': Permission.VIEW_CHAT,
};

// Check if user can see a navigation item
export const canSeeNavigationItem = (userRole, itemName) => {
  if (!userRole || !itemName) return false;
  const permission = NAVIGATION_PERMISSIONS[itemName];
  return permission ? hasPermission(userRole, permission) : true;
};

// Get role display name
export const getRoleDisplayName = (role) => {
  const displayNames = {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.MANAGER]: 'Manager',
    [UserRole.ACCOUNTANT]: 'Accountant',
    [UserRole.TEACHER]: 'Teacher',
  };
  return displayNames[role] || role;
};

// Get role color for UI
export const getRoleColor = (role) => {
  const colors = {
    [UserRole.ADMIN]: 'bg-red-100 text-red-800',
    [UserRole.MANAGER]: 'bg-blue-100 text-blue-800',
    [UserRole.ACCOUNTANT]: 'bg-green-100 text-green-800',
    [UserRole.TEACHER]: 'bg-yellow-100 text-yellow-800',
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
}; 